import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Eye, EyeOff, Lock, Mail } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLogin } from "@/hooks/use-auth";
import { toUserMessage } from "@/lib/api-client";
import { CONTACTS } from "@/lib/constants";
import logo from "@/assets/logo-rosomaha.webp";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const { mutate: login, isPending, error } = useLogin();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    login({ email, password });
  };

  return (
    <div className="page-shell flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="section-block p-4 md:p-6">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img src={logo} alt="Логотип" className="h-16" />
          </div>

          {/* Header */}
          <div className="mb-6 text-center">
            <h1 className="page-title">Вход в портал</h1>
            <p className="page-subtitle">Введите данные для входа в систему</p>
          </div>

          {/* Server error */}
          {error && (
            <div
              role="alert"
              className="mb-4 rounded-lg bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive"
            >
              {toUserMessage(error)}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-muted-foreground" />
                <Input
                  id="email"
                  autoComplete="username"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-icon"
                  required
                  disabled={isPending}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Пароль</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-muted-foreground" />
                <Input
                  id="password"
                  autoComplete="current-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-icon pr-icon"
                  required
                  disabled={isPending}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Скрыть пароль" : "Показать пароль"}
                  className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-primary"
                >
                  {showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                </button>
              </div>
            </div>

            {/* There is no self-service password reset (no endpoint in api/openapi.yaml);
                the manager resets it, so point straight at them. */}
            <p className="text-sm text-muted-foreground">
              Забыли пароль? Позвоните менеджеру:{" "}
              <a
                href={CONTACTS.phoneHref}
                className="text-primary hover:underline whitespace-nowrap"
              >
                {CONTACTS.phone}
              </a>
            </p>

            <Button type="submit" size="lg" className="w-full" disabled={isPending}>
              {isPending ? (
                "Вход..."
              ) : (
                <>
                  Войти
                  <ArrowRight className="size-4" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">Нет аккаунта? </span>
            <Link to="/register" className="text-primary font-medium hover:underline">
              Зарегистрироваться
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
